import { useSnackbarContext } from '@/contexts/SnackbarContext';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { useState } from 'react';
import { OnboardingChips } from './OnboardingChips';
import { StepContentLayout } from './StepContentLayout';
import { OnboardingFormData, StepContentProps } from './StepContentProps';

export const OnboardingStep3 = ({
  data,
  onChange,
  onNext,
  onBack,
  isLoading = false,
  disabled,
}: StepContentProps) => {
  const { show } = useSnackbarContext();
  const [userRole, setUserRole] = useState<OnboardingFormData['userRole'] | ''>(
    data.userRole || '',
  );
  const [companySize, setCompanySize] = useState<
    OnboardingFormData['companySize'] | ''
  >(data.companySize || '');
  const [industries, setIndustries] = useState<string[]>(data.industries || []);

  const handleSubmit = () => {
    if (!userRole) {
      show('Please select your role to continue.', 'warn');
      return;
    }
    if (!companySize) {
      show('Please select company size to continue.', 'warn');
      return;
    }

    onNext();
  };

  const roleOptions = [
    { value: 'marketing', label: 'Marketing' },
    { value: 'product', label: 'Product' },
    { value: 'growth', label: 'Growth' },
    { value: 'exec', label: 'Executive' },
    { value: 'founder', label: 'Founder' },
    { value: 'other', label: 'Other' },
  ];

  const companySizeOptions = [
    { value: '1-10', label: '1-10' },
    { value: '11-50', label: '11-50' },
    { value: '51-200', label: '51-200' },
    { value: '201-500', label: '201-500' },
    { value: '500-999', label: '500-999' },
    { value: '1000+', label: '1,000+' },
  ];

  const industryOptions = [
    'E-commerce',
    'SaaS',
    'Education',
    'Healthcare',
    'Finance',
    'Real Estate',
    'Travel & Hospitality',
    'Automotive',
    'Entertainment',
    'Technology',
    'Consulting',
    'Other',
  ];

  return (
    <StepContentLayout
      showBackButton={!!onBack}
      onBack={onBack}
      onNext={handleSubmit}
      isLoading={isLoading}
      nextButtonDisabled={!userRole || !companySize}
      disabled={disabled}
    >
      <div className="flex flex-col gap-5">
        {/* Your Role */}
        <div className="flex flex-col gap-2">
          <FieldTitle title="Your Role" />
          <Select
            value={userRole}
            onValueChange={(value) => {
              setUserRole(value as OnboardingFormData['userRole']);
              onChange((prev) => ({
                ...prev,
                userRole: value as OnboardingFormData['userRole'],
              }));
            }}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select your role" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Company Size */}
        <OnboardingChips
          label="Company Size"
          options={companySizeOptions.map((o) => o.label)}
          value={companySize || ''}
          onChange={(value) => {
            const option = companySizeOptions.find((o) => o.label === value);
            setCompanySize(option?.value as any);
            onChange((prev) => ({
              ...prev,
              companySize: option?.value as OnboardingFormData['companySize'],
            }));
          }}
          disabled={disabled}
        />

        {/* Industry */}
        <OnboardingChips
          label="Industry"
          optional
          options={industryOptions}
          value={industries}
          onChange={(value) => {
            setIndustries(value as string[]);
            onChange((prev) => ({
              ...prev,
              industries: value as string[],
            }));
          }}
          multiSelect
          disabled={disabled}
        />
      </div>
    </StepContentLayout>
  );
};
